import { useMemo, useState } from "react";
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatCurrency } from "@/lib/format";
import { parseDateMs } from "./utils";
import { StatusPill } from "./Atoms";
import UnitDocCell from "./UnitDocCell";

// Coerce unit cells like '7', '7.0', 'Unit 7' to a canonical string so the
// dashboard cell looks up the same shared backend document as Building B.
function normalizeUnit(u) {
  if (u == null) return "";
  const s = String(u).trim();
  const m = s.match(/-?\d+/);
  return m ? String(parseInt(m[0], 10)) : s.toLowerCase();
}

export function RevenueTableBody({ items, unitDocs = {}, canEditDocs = false, canDeleteDocs = false, onDocUploaded = () => {} }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("sale_date");
  const [sortDir, setSortDir] = useState("asc");

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const statusOptions = useMemo(() => {
    const set = new Set();
    items.forEach((it) => {
      const s = (it.closing_status || "").trim();
      const lower = s.toLowerCase();
      if (!s) set.add("Remaining");
      else if (lower.includes("closed")) set.add("Closed");
      else if (lower.includes("contract")) set.add("In Contract");
      else set.add(s);
    });
    return ["all", ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = items;
    if (statusFilter !== "all") {
      list = list.filter((it) => {
        const s = (it.closing_status || "").trim().toLowerCase();
        if (statusFilter === "Closed") return s.includes("closed");
        if (statusFilter === "In Contract") return s.includes("contract");
        if (statusFilter === "Remaining") return s === "";
        return s === statusFilter.toLowerCase();
      });
    }
    if (q) {
      list = list.filter((it) =>
        (it.class_type || "").toLowerCase().includes(q) ||
        (it.closing_status || "").toLowerCase().includes(q) ||
        (it.sale_date || "").toLowerCase().includes(q) ||
        (it.ooa_date || "").toLowerCase().includes(q) ||
        String(it.unit_number || "").toLowerCase().includes(q) ||
        (it.name || "").toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      let av, bv;
      if (sortKey === "sale_date") { av = parseDateMs(a.sale_date); bv = parseDateMs(b.sale_date); }
      else if (sortKey === "gross_revenue") { av = a.gross_revenue || 0; bv = b.gross_revenue || 0; }
      else { av = 0; bv = 0; }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [items, search, statusFilter, sortKey, sortDir]);

  const SortHeader = ({ k, children, align = "left" }) => {
    const active = sortKey === k;
    return (
      <th className={`px-1 sm:px-5 py-2 text-${align} text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider`}>
        <button
          type="button"
          onClick={() => toggleSort(k)}
          className={`inline-flex items-center gap-0.5 sm:gap-1 hover:text-slate-700 ${active ? "text-slate-800" : ""}`}
          data-testid={`revenue-sort-${k}`}
        >
          {children}
          <CaretDown
            size={11}
            weight="bold"
            className={`transition-transform ${active ? "" : "opacity-30"} ${active && sortDir === "asc" ? "rotate-180" : ""}`}
          />
        </button>
      </th>
    );
  };

  return (
    <>
      <div className="p-3 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search units…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
            data-testid="revenue-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-10 w-full sm:w-52 border-slate-300" data-testid="revenue-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt === "all" ? "All statuses" : opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] sm:text-sm">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr>
              <th className="px-1 sm:px-5 py-2 text-left text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">Class Type</th>
              <th className="px-1 sm:px-5 py-2 text-left text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">Closing Status</th>
              <th className="px-1 sm:px-5 py-2 text-left text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">OOA Date</th>
              <SortHeader k="sale_date">Sale Date</SortHeader>
              <th className="px-1 sm:px-5 py-2 text-left text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">Unit Number</th>
              <th className="px-1 sm:px-5 py-2 text-left text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">Contract</th>
              <th className="px-1 sm:px-5 py-2 text-left text-[9px] sm:text-xs font-semibold text-slate-500 uppercase tracking-tight sm:tracking-wider">Closing Statement</th>
              <SortHeader k="gross_revenue" align="right">Gross Revenue</SortHeader>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-1 sm:px-5 py-6 text-center text-sm text-slate-400">No matching units.</td>
              </tr>
            ) : filtered.map((it, idx) => {
              const u = normalizeUnit(it.unit_number);
              return (
              <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50 align-top" data-testid={`revenue-row-${idx}`}>
                <td className="px-1 sm:px-5 py-[5px] text-slate-800 font-medium">{it.class_type}</td>
                <td className="px-1 sm:px-5 py-[5px]"><StatusPill status={it.closing_status} /></td>
                <td className="px-1 sm:px-5 py-[5px] text-slate-700 tabular-nums">{it.ooa_date || "—"}</td>
                <td className="px-1 sm:px-5 py-[5px] text-slate-700 tabular-nums">{it.sale_date || "—"}</td>
                <td className="px-1 sm:px-5 py-[5px] text-slate-700 tabular-nums">{it.unit_number || "—"}</td>
                <td className="px-1 sm:px-5 py-[5px]" style={{ minWidth: 180, maxWidth: 220 }}>
                  {u ? (
                    <UnitDocCell
                      unit={u}
                      kind="contract"
                      doc={unitDocs[`${u}|contract`]}
                      canEdit={canEditDocs}
                      canDelete={canDeleteDocs}
                      onUploaded={(fresh) => onDocUploaded(u, "contract", fresh)}
                    />
                  ) : <span className="text-slate-300 text-[10px]">—</span>}
                </td>
                <td className="px-1 sm:px-5 py-[5px]" style={{ minWidth: 180, maxWidth: 220 }}>
                  {u ? (
                    <UnitDocCell
                      unit={u}
                      kind="closing_statement"
                      doc={unitDocs[`${u}|closing_statement`]}
                      canEdit={canEditDocs}
                      canDelete={canDeleteDocs}
                      onUploaded={(fresh) => onDocUploaded(u, "closing_statement", fresh)}
                    />
                  ) : <span className="text-slate-300 text-[10px]">—</span>}
                </td>
                <td className="px-1 sm:px-5 py-[5px] text-right tabular-nums text-slate-900 font-semibold">{formatCurrency(it.gross_revenue)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function VendorTableBody({ data, loading, error, onRetry }) {
  const [search, setSearch] = useState("");
  const [totalSort, setTotalSort] = useState(null);
  const cycleTotalSort = () => setTotalSort((s) => (s === null ? "desc" : s === "desc" ? "asc" : null));

  const headers = data?.headers || [];
  const items = data?.items || [];

  // Relabel any sheet header that reads "Total Unpaid" / "Unpaid" → "Total Payables"
  const displayHeader = (h) => {
    if (!h) return "—";
    const lower = h.trim().toLowerCase();
    if (lower === "total unpaid" || lower === "unpaid total" || lower === "unpaid") return "Total Payables";
    return h;
  };

  const totalKey = useMemo(() => {
    const keys = ["col_k", "col_l", "col_m"];
    for (let i = 1; i < headers.length; i++) {
      if ((headers[i] || "").trim().toLowerCase() === "total") return keys[i - 1];
    }
    for (let i = 1; i < headers.length; i++) {
      const h = (headers[i] || "").trim().toLowerCase();
      if (h.startsWith("total") && !h.includes("paid") && !h.includes("unpaid")) return keys[i - 1];
    }
    return "col_m";
  }, [headers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = items.filter((it) => !q || (it.name || "").toLowerCase().includes(q));
    if (totalSort === "desc") list = [...list].sort((a, b) => (b[totalKey] || 0) - (a[totalKey] || 0));
    else if (totalSort === "asc") list = [...list].sort((a, b) => (a[totalKey] || 0) - (b[totalKey] || 0));
    return list;
  }, [items, search, totalSort, totalKey]);

  if (loading) {
    return (
      <div className="px-5 py-4 space-y-2" data-testid="vendor-skeleton">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="grid grid-cols-4 gap-3">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-5 py-8 text-center">
        <div className="text-sm text-[#D92D20] mb-2">{error}</div>
        <Button onClick={onRetry} variant="outline" className="border-slate-300">Try again</Button>
      </div>
    );
  }
  if (!data || data.notConfigured) return <div className="px-5 py-8 text-sm text-slate-400 text-center">No SummaryVendor tab data available.</div>;
  if (!headers.length) return <div className="px-5 py-8 text-sm text-slate-400 text-center">SummaryVendor tab not found in workbook.</div>;

  const totalIdx = ["col_k", "col_l", "col_m"].indexOf(totalKey) + 1;

  return (
    <>
      <div className="p-3 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search vendors…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10"
            data-testid="vendor-search-input"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] sm:text-sm">
          <thead className="bg-slate-50 border-y border-slate-200">
            <tr>
              <th className="px-2 sm:px-5 py-2 text-left text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">{displayHeader(headers[0]) || "Vendor"}</th>
              {[1, 2, 3].map((i) => {
                const isTotal = i === totalIdx;
                return (
                  <th key={i} className="px-2 sm:px-5 py-2 text-right text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {isTotal ? (
                      <button
                        type="button"
                        onClick={cycleTotalSort}
                        className={`inline-flex items-center gap-1 hover:text-slate-700 ${totalSort ? "text-slate-800" : ""}`}
                        data-testid="vendor-total-sort"
                      >
                        {displayHeader(headers[i])}
                        <CaretDown
                          size={11}
                          weight="bold"
                          className={`transition-transform ${totalSort ? "" : "opacity-30"} ${totalSort === "asc" ? "rotate-180" : ""}`}
                        />
                      </button>
                    ) : displayHeader(headers[i])}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={4} className="px-2 sm:px-5 py-6 text-center text-sm text-slate-400">No matching vendors.</td></tr>
            ) : filtered.map((it, idx) => (
              <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`vendor-row-${idx}`}>
                <td className="px-2 sm:px-5 py-[5px] text-slate-800 font-medium">{it.name}</td>
                <td className={`px-2 sm:px-5 py-[5px] text-right tabular-nums ${totalIdx === 1 ? "text-slate-900 font-semibold" : "text-slate-700"}`}>{formatCurrency(it.col_k)}</td>
                <td className={`px-2 sm:px-5 py-[5px] text-right tabular-nums ${totalIdx === 2 ? "text-slate-900 font-semibold" : "text-slate-700"}`}>{formatCurrency(it.col_l)}</td>
                <td className={`px-2 sm:px-5 py-[5px] text-right tabular-nums ${totalIdx === 3 ? "text-slate-900 font-semibold" : "text-slate-700"}`}>{formatCurrency(it.col_m)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
